import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import axiosInstance from "../../../services/AxiosInstance";
import swal from "sweetalert";
import { format } from "date-fns";

const REASONS = ["too long", "wrong tone", "off topic", "inaccurate", "duplicate", "low quality"];
const errMsg = (e) => e?.response?.data?.message || e.message || "Something went wrong";
const scoreColor = (s) => (s >= 80 ? "success" : s >= 60 ? "warning" : "danger");

const AutopilotReview = ({ onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState({}); // id -> text
  const [rejecting, setRejecting] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("autopilot/review");
      setItems(data.data || []);
    } catch (e) { swal("Error", errMsg(e), "error"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const done = () => { load(); onChanged && onChanged(); };

  const approve = async (it) => {
    setBusy(it._id);
    try {
      const { data } = await axiosInstance.post(`autopilot/review/${it._id}/approve`, editing[it._id] !== undefined ? { text: editing[it._id] } : {});
      if (data.suggestAuto) swal("Great job", "10 clean approvals in a row. You can now switch this source to Full Auto from the dashboard.", "success");
      done();
    } catch (e) { swal("Cannot approve", errMsg(e), "error"); }
    setBusy("");
  };
  const reject = async (it, reason) => {
    setBusy(it._id); setRejecting("");
    try { await axiosInstance.post(`autopilot/review/${it._id}/reject`, { reason }); done(); }
    catch (e) { swal("Error", errMsg(e), "error"); }
    setBusy("");
  };
  const regenerate = async (it) => {
    setBusy(it._id);
    try { await axiosInstance.post(`autopilot/review/${it._id}/regenerate`); setEditing((x) => { const n = { ...x }; delete n[it._id]; return n; }); done(); }
    catch (e) { swal("Cannot regenerate", errMsg(e), "error"); }
    setBusy("");
  };
  const approveAll = async () => {
    try {
      const { data } = await axiosInstance.post("autopilot/review/approve-all", { minScore: 80 });
      swal("Done", data.message, "success"); done();
    } catch (e) { swal("Error", errMsg(e), "error"); }
  };

  if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
  if (!items.length) return (
    <div className="card"><div className="card-body text-center py-5 text-muted">
      <h5>Nothing to review</h5><p className="mb-0">New AI drafts will show up here. Check back soon.</p>
    </div></div>
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">{items.length} waiting for review</h5>
        <button className="btn btn-outline-primary btn-sm" onClick={approveAll}>Approve all with score 80+</button>
      </div>
      {items.map((it) => {
        const isEdit = editing[it._id] !== undefined;
        return (
          <div className="card mb-3" key={it._id}>
            <div className="card-body">
              <div className="d-flex gap-3 flex-wrap flex-md-nowrap">
                {it.imageUrl && (
                  <img src={it.imageUrl} alt="" referrerPolicy="no-referrer"
                    style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 8 }} onError={(e) => (e.target.style.display = "none")} />
                )}
                <div className="flex-fill">
                  <div className="mb-2">
                    <span className={`badge badge-${scoreColor(it.score)} mr-2`}>Score {it.score}</span>
                    <span className="text-muted small">{it.sourceName}{it.createdAt ? ` · ${format(new Date(it.createdAt), "dd MMM HH:mm")}` : ""}</span>
                  </div>
                  {isEdit ? (
                    <textarea className="form-control" rows={5} value={editing[it._id]} onChange={(e) => setEditing({ ...editing, [it._id]: e.target.value })} />
                  ) : (
                    <div style={{ whiteSpace: "pre-wrap" }}>{it.finalText}</div>
                  )}
                  {it.issues?.length > 0 && <div className="small text-warning mt-2">AI notes: {it.issues.join("; ")}</div>}
                  {it.reason && <div className="small text-danger mt-1">{it.reason}</div>}
                </div>
              </div>
              <div className="d-flex gap-2 mt-3 flex-wrap">
                <button className="btn btn-primary btn-sm" disabled={busy === it._id} onClick={() => approve(it)}>
                  {busy === it._id ? <Spinner animation="border" size="sm" /> : isEdit ? "Save & approve" : "Approve"}
                </button>
                {!isEdit && <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditing({ ...editing, [it._id]: it.editedText || it.aiText })}>Edit</button>}
                {isEdit && <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditing((x) => { const n = { ...x }; delete n[it._id]; return n; })}>Cancel edit</button>}
                <button className="btn btn-outline-secondary btn-sm" disabled={busy === it._id} onClick={() => regenerate(it)}>Regenerate</button>
                <div className="dropdown">
                  <button className="btn btn-outline-danger btn-sm" onClick={() => setRejecting(rejecting === it._id ? "" : it._id)}>Reject ▾</button>
                  {rejecting === it._id && (
                    <div className="dropdown-menu show" style={{ position: "absolute" }}>
                      {REASONS.map((r) => <button key={r} className="dropdown-item" onClick={() => reject(it, r)}>{r}</button>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default AutopilotReview;
